import { AlertCircle, Crop, FileText, Loader2, RotateCcw, RotateCw, Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';

const STATUS_LABEL = {
  queued: 'Ready',
  preparing: 'Preparing',
  processing: 'Processing',
  done: 'Extracted',
  failed: 'Failed',
};

const STATUS_STYLE = {
  queued: 'bg-slate-100 text-slate-700',
  preparing: 'bg-indigo-50 text-indigo-700',
  processing: 'bg-indigo-50 text-indigo-700',
  done: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-rose-50 text-rose-700',
};

const kb = (bytes) => (bytes > 1024 * 1024 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`);

export const FileQueue = ({
  items,
  isRunning,
  onRotate,
  onCrop,
  onRemove,
  onExtractOne,
  onRetryOtherEngine,
  onRetryOtherMode,
  onRetryWithClassic,
  canUseNextGen,
  mode,
}) => {
  if (!items.length) return null;

  return (
    <section aria-labelledby="queue-heading" className="scroll-mt-24" data-testid="file-queue-container" id="queue">
      <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" id="queue-heading">
        File queue
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        {items.length} file{items.length > 1 ? 's' : ''} ready. Files are processed one at a time so a single
        failure never stops the rest.
      </p>

      <ul className="mt-4 grid gap-3">
        {items.map((item) => (
          <li className="surface-card p-4" data-testid={`queue-item-${item.id}`} key={item.id}>
            <div className="flex gap-4">
              <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-[#e6e9f4] bg-slate-50">
                {item.previewUrl ? (
                  <img
                    alt={`Preview of ${item.name}`}
                    className="h-full w-full object-cover"
                    src={item.previewUrl}
                    style={{ transform: `rotate(${item.rotation}deg)` }}
                  />
                ) : (
                  <span className="flex h-full w-full items-center justify-center text-slate-400">
                    <FileText aria-hidden="true" className="h-8 w-8" />
                  </span>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900" title={item.name}>
                      {item.name}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {item.kind === 'pdf' ? 'PDF' : `${item.width}×${item.height}`} · {kb(item.sizeBytes)}
                      {item.crop ? ' · cropped' : ''}
                      {item.rotation ? ` · rotated ${item.rotation}°` : ''}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_STYLE[item.status]}`}
                    data-testid={`queue-status-${item.id}`}
                  >
                    {STATUS_LABEL[item.status]}
                  </span>
                </div>

                {item.status === 'processing' ? (
                  <div className="mt-3">
                    <Progress className="h-1.5" value={Math.round((item.progress || 0) * 100)} />
                    <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-indigo-700">
                      <Loader2 aria-hidden="true" className="h-3 w-3 animate-spin" />
                      {item.statusText || 'Working'} · {Math.round((item.progress || 0) * 100)}%
                    </p>
                  </div>
                ) : null}

                {item.status === 'done' ? (
                  <p className="mt-2 text-xs font-medium text-emerald-700">{item.statusText}</p>
                ) : null}

                {item.error ? (
                  <div className="mt-3 rounded-xl bg-rose-50 px-3 py-2" data-testid={`queue-error-${item.id}`}>
                    <p className="flex items-start gap-2 text-xs font-semibold text-rose-800">
                      <AlertCircle aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      {item.error.message}
                    </p>
                    <p className="mt-1 font-mono text-[11px] text-rose-600">Code: {item.error.code}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {item.error.engine === 'nextgen' ? (
                        <Button
                          className="brand-gradient text-white"
                          data-testid={`retry-with-classic-${item.id}`}
                          disabled={isRunning}
                          onClick={() => onRetryWithClassic(item.id)}
                          size="sm"
                        >
                          Retry with Classic OCR
                        </Button>
                      ) : null}
                      <Button
                        data-testid={`retry-button-${item.id}`}
                        disabled={isRunning}
                        onClick={() => onExtractOne(item.id)}
                        size="sm"
                        variant="outline"
                      >
                        Retry
                      </Button>
                      <Button
                        data-testid={`retry-other-mode-${item.id}`}
                        disabled={isRunning}
                        onClick={() => onRetryOtherMode(item.id)}
                        size="sm"
                        variant="outline"
                      >
                        Retry in {mode === 'document' ? 'Poster' : 'Document'} mode
                      </Button>
                      {canUseNextGen ? (
                        <Button
                          data-testid={`retry-other-engine-${item.id}`}
                          disabled={isRunning}
                          onClick={() => onRetryOtherEngine(item.id)}
                          size="sm"
                          variant="outline"
                        >
                          Retry with the other engine
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                <div className="mt-3 flex flex-wrap gap-2">
                  {item.kind === 'image' ? (
                    <>
                      <Button
                        aria-label={`Rotate ${item.name} left`}
                        data-testid={`file-item-rotate-left-${item.id}`}
                        disabled={isRunning}
                        onClick={() => onRotate(item.id, -90)}
                        size="sm"
                        variant="outline"
                      >
                        <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        aria-label={`Rotate ${item.name} right`}
                        data-testid={`file-item-rotate-right-${item.id}`}
                        disabled={isRunning}
                        onClick={() => onRotate(item.id, 90)}
                        size="sm"
                        variant="outline"
                      >
                        <RotateCw aria-hidden="true" className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        data-testid={`file-item-crop-button-${item.id}`}
                        disabled={isRunning}
                        onClick={() => onCrop(item.id)}
                        size="sm"
                        variant="outline"
                      >
                        <Crop aria-hidden="true" className="mr-1.5 h-3.5 w-3.5" />
                        Crop
                      </Button>
                    </>
                  ) : null}
                  <Button
                    className="brand-gradient text-white"
                    data-testid={`extract-one-button-${item.id}`}
                    disabled={isRunning}
                    onClick={() => onExtractOne(item.id)}
                    size="sm"
                  >
                    Extract Text
                  </Button>
                  <Button
                    aria-label={`Remove ${item.name} from the queue`}
                    data-testid={`file-item-remove-button-${item.id}`}
                    onClick={() => onRemove(item.id)}
                    size="sm"
                    variant="ghost"
                  >
                    <X aria-hidden="true" className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

export const QueueToolbar = ({ items, isRunning, onExtractAll, onCancel, onClearAll }) => {
  if (!items.length) return null;
  return (
    <div className="surface-card sticky bottom-3 z-20 flex flex-wrap items-center gap-3 p-4" data-testid="action-bar">
      <Button
        className="brand-gradient flex-1 text-white sm:flex-none"
        data-testid="extract-all-button"
        disabled={isRunning}
        onClick={onExtractAll}
        size="lg"
      >
        {isRunning ? (
          <>
            <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
            Extracting…
          </>
        ) : (
          `Extract All (${items.length})`
        )}
      </Button>
      {isRunning ? (
        <Button data-testid="cancel-button" onClick={onCancel} size="lg" variant="outline">
          Cancel
        </Button>
      ) : null}
      <Button data-testid="clear-all-queue-button" onClick={onClearAll} size="lg" variant="ghost">
        <Trash2 aria-hidden="true" className="mr-2 h-4 w-4" />
        Clear queue
      </Button>
    </div>
  );
};

export default FileQueue;
